import { computed, inject, Injectable, signal } from '@angular/core';
import { ApiErrorResponse, ErrorHandlerV2Service, SelectOption } from '@gea/digital-ui-lib';
import { catchError, finalize, first, of } from 'rxjs';

import { MachinesApiService } from '@shared/services';
import { MachineAndFaultDto } from '@brc/shared/models';
import { FaultsStore } from '../faults';
import { FAULT_ANALYSIS_SESSION_STORAGE_KEY } from '@tab-container/fault-analysis/';

export interface MachineDTO {
  nameInCRM: string;
  equipmentType: string;
  omniId?: string;
  contactPersonName?: string;
  contactPersonEmail?: string;
  contactPersonPhone?: string;
  lastServiced?: string;
  openServicesCount?: number;
  nextServiceDateInsertedByUser?: string;
  nextServiceDateRecomendedByBRC?: string;
  organizationId: number;
  organizationName: string;
  components: string[];
  id: number;
  name: string;
  status: string;
  isAvailable: boolean;
  online: boolean;
  omniSWVersion: string;
  edgeGatewaySerialNumber: string;
  ipAddress?: string | null;
  productionLine?: string;
  warningsCount?: number;
  shutdownsCount?: number;
}

export interface MachinesDTOResponse {
  machines: MachineDTO[];
  isOmniDeprecated: boolean;
  omniMinRequiredVersion?: string;
}

export interface MachineNotification {
  timeStamp: string;
  type: string;
  description: string;
  duration: string;
  count: number;
}

export interface MachineDetails extends MachineDTO {
  series: string;
  manufacturer: string;
  yearOfManufacturing: string;
  imgURL: string;
  notifications: MachineNotification[];
  availability?: string;
  lastSignalReceived?: string;
}

export interface MachineNameCRM {
  id: number;
  name: string;
  notifications: { timestamp: string; type: 'warning' | 'error' | 'info' }[];
}

export interface MachineOverviewModel extends MachineDTO {
  shutdownsCount?: number;
  warningsCount?: number;
}

export interface MachinesStoreState {
  isLoading: boolean;
  isOmniDeprecated?: boolean;
  omniMinRequiredVersion?: string;
  machines?: SelectOption[];
  machineAndFaults?: MachineAndFaultDto[];
  machineOverviewTableData?: MachineOverviewModel[];
}

const INITIAL_STATE: MachinesStoreState = { isLoading: false };

const EMPTY_MACHINES_RESPONSE: MachinesDTOResponse = {
  machines: [],
  isOmniDeprecated: false,
  omniMinRequiredVersion: undefined,
};

@Injectable({ providedIn: 'root' })
export class MachinesStore {
  private readonly machinesApiService = inject(MachinesApiService);
  private readonly errorHandler = inject(ErrorHandlerV2Service);
  private readonly faultsStore = inject(FaultsStore);

  private readonly state = signal<MachinesStoreState>(INITIAL_STATE);

  readonly isLoading = computed(() => this.state().isLoading);
  readonly isOmniDeprecated = computed(() => this.state().isOmniDeprecated);
  readonly omniMinRequiredVersion = computed(() => this.state().omniMinRequiredVersion);
  readonly machines = computed(() => this.state().machines);
  readonly machineAndFaults = computed(() => this.state().machineAndFaults);
  readonly machineOverviewData = computed<MachineOverviewModel[]>(() => this.state().machineOverviewTableData ?? []);

  fetchMachines(): void {
    this.patch({
      machines: undefined,
      omniMinRequiredVersion: undefined,
      machineAndFaults: undefined,
      machineOverviewTableData: undefined,
      isLoading: true,
    });

    this.machinesApiService
      .fetchMachines()
      .pipe(
        catchError((error: ApiErrorResponse) => {
          this.errorHandler.handleError(error);
          return of(EMPTY_MACHINES_RESPONSE);
        }),
        first(),
        finalize(() => this.patch({ isLoading: false }))
      )
      .subscribe((response) => {
        const machines: SelectOption[] = response.machines.map(({ name, id: value }) => ({ name, value }));

        this.patch({
          isOmniDeprecated: response.isOmniDeprecated,
          omniMinRequiredVersion: response.omniMinRequiredVersion,
          machineOverviewTableData: response.machines.map((machine) => ({ ...machine })),
          machines,
        });

        this.selectFaultAnalysisMachines(machines);
      });
  }

  private patch(partial: Partial<MachinesStoreState>): void {
    this.state.update((current) => ({ ...current, ...partial }));
  }

  private selectFaultAnalysisMachines(machines: SelectOption[]): void {
    let selectedMachines = machines.map(({ value }) => value as number);
    const selectedMachineSet = new Set<number>(selectedMachines);
    const storedMachines = this.getSelectedMachinesForFaultAnalysis();

    // only use machines from the sessionStorage if all stored machines are present in the fetched machines
    // this makes sure the selected machines belong to the selected org
    if (storedMachines.length && storedMachines.every((machine: number) => selectedMachineSet.has(machine))) {
      selectedMachines = storedMachines;
    }

    this.faultsStore.setSelectedFaultAnalysisState({ selectedMachines });
  }

  private getSelectedMachinesForFaultAnalysis(): number[] {
    const storedMachines = sessionStorage.getItem(FAULT_ANALYSIS_SESSION_STORAGE_KEY);

    if (!storedMachines) {
      return [];
    }

    try {
      const parsed = JSON.parse(storedMachines);
      return parsed.selectedMachines ? parsed.selectedMachines : [];
    } catch {
      sessionStorage.removeItem(FAULT_ANALYSIS_SESSION_STORAGE_KEY);
      return [];
    }
  }
}
